/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "flowbite-react";
import { HiOutlinePlus } from "react-icons/hi";
import {
  Dialog,
  LGBDatepicker,
  AddressSearchInput,
  AddressSearchResult,
} from "@components";
import {
  ILocation,
  IRouteItem,
  IRouteTiming,
  ICustomer,
} from "../../../../types";
import {
  getPublicFormInputClass,
  getPublicFormLabelClass,
} from "../publicFormFieldClasses";

export interface PublicRouteSubmitData {
  location: Partial<ILocation>;
  isPickup: boolean;
  stopDate: Date;
  timing: string;
  beforeTime?: string;
  afterTime?: string;
}

export interface PublicRouteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: PublicRouteSubmitData) => void;
  data: Partial<IRouteItem>;
  title: string;
  allowPickupSelection?: boolean;
  accessToken?: string;
  workspaceId?: string;
  config?: any;
  customer?: Partial<ICustomer>;
}

const PublicRouteForm: React.FC<Omit<PublicRouteModalProps, "isOpen">> = ({
  onClose,
  onConfirm,
  data,
  allowPickupSelection = true,
  accessToken,
  workspaceId,
  config,
  customer,
}) => {
  const { t } = useTranslation(["order_form"]);
  const [address, setAddress] = useState("");
  const [postCode, setPostCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("NO");
  const [description, setDescription] = useState("");
  const [isPickup, setIsPickup] = useState(allowPickupSelection ? true : false);
  const [date, setDate] = useState(new Date());
  const [beforeTime, setBeforeTime] = useState("17:00");
  const [afterTime, setAfterTime] = useState("08:00");
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  // Track selected place id to ensure user picked a suggestion
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isAddressValid, setIsAddressValid] = useState(false);

  // Function to generate time options for every 15 minutes
  const generateTimeOptions = (): string[] => {
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedHour = hour.toString().padStart(2, "0");
        const formattedMinute = minute.toString().padStart(2, "0");
        times.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return times;
  };

  const getTimingValue = (timing: IRouteTiming | undefined): string => {
    if (!timing) return "4";
    if (timing.earliest) return "4";
    if (timing.latest) return "5";
    if (timing.before && timing.after) return "2";
    if (timing.before) return "1";
    if (timing.after) return "3";
    return "4";
  };

  const [timing, setTiming] = useState("4");

  useEffect(() => {
    setAddress(data.location?.addressLine || "");
    setPostCode(data.location?.postCode || "");
    setCity(data.location?.city || "");
    setCountry(data.location?.countryCode || "NO");
    setDescription(data.location?.description || "");
    setIsPickup(data.isPickup ?? true);
    setDate(data.stopDate ? data.stopDate.toDate() : new Date());
    // Using 'as any' since placeId is not in ILocation interface for now
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setPlaceId((data.location as any)?.placeId ?? null);
    // An existing stop already has a validated address, so mark it valid to
    // avoid forcing the user to re-select the address on edit.
    setIsAddressValid(!!data.location?.addressLine);

    // Set timing and time values
    const timingValue = getTimingValue(data.timing);
    setTiming(timingValue);

    if (data.timing?.before) {
      setBeforeTime(data.timing.before);
    }
    if (data.timing?.after) {
      setAfterTime(data.timing.after);
    }
  }, [data]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, boolean> = {};

    if (!address || address.trim() === "" || !isAddressValid) {
      newErrors.address = true;
    }
    if (!postCode || postCode.trim() === "") {
      newErrors.postCode = true;
    }
    if (!city || city.trim() === "") {
      newErrors.city = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (validateForm()) {
      const submitData = {
        // Cast to 'any' to include placeId which is not part of ILocation yet
        location: {
          displayName: address,
          addressLine: address,
          postCode,
          city,
          countryCode: country,
          description,
          placeId: placeId || undefined,
        } as any,
        isPickup,
        stopDate: date,
        timing,
        beforeTime,
        afterTime,
      };
      onConfirm(submitData);
    }
  };

  const inputClass = (fieldName: string) =>
    getPublicFormInputClass(errors, fieldName);

  const labelClass = (fieldName: string) =>
    getPublicFormLabelClass(errors, fieldName);

  const handleAddressSelect = (result: AddressSearchResult) => {
    setAddress(result.addressLine);
    setPostCode(result.postCode);
    setCity(result.city);
    setCountry(result.countryCode || "NO");
    setPlaceId(result.placeId || null);
    setErrors((prev) => ({ ...prev, address: false }));
  };

  const handleAddressValidated = (isValid: boolean) => {
    setIsAddressValid(isValid);
    if (!isValid) {
      setErrors((prev) => ({ ...prev, address: true }));
    } else {
      setErrors((prev) => ({ ...prev, address: false }));
    }
  };

  const handleUseCustomerAddress = () => {
    if (customer?.primaryAddress) {
      setAddress(customer.primaryAddress.addressLine || "");
      setPostCode(customer.primaryAddress.postCode || "");
      setCity(customer.primaryAddress.city || "");
      setCountry(customer.primaryAddress.countryCode || "NO");
      setIsAddressValid(true);
      setErrors((prev) => ({ ...prev, address: false }));
    }
  };

  const shouldShowCustomerSuggestion = () => {
    return customer?.primaryAddress?.addressLine && !address.trim();
  };

  const handleFieldChange = (field: string, value: string) => {
    switch (field) {
      case "address":
        setAddress(value);
        break;
      case "postCode":
        setPostCode(value);
        break;
      case "city":
        setCity(value);
        break;
      case "country":
        setCountry(value);
        break;
      case "description":
        setDescription(value);
        break;
    }

    // Clear error when user starts typing
    if (hasSubmitted) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      id="public-route-form"
      className="space-y-4 min-w-[600px]"
    >
      <div className="relative">
        <Label
          htmlFor="newLocationAddress"
          value={`${t("public_form.common.address")} *`}
          className={labelClass("address")}
        />
        <div className="space-y-2">
          <AddressSearchInput
            id="newLocationAddress"
            name="newLocationAddress"
            placeholder={t("public_form.common.address_placeholder")}
            value={address}
            onChange={setAddress}
            onSelectAddress={handleAddressSelect}
            onAddressValidated={handleAddressValidated}
            required={true}
            autoFocus
            initiallyValidated={!!data.location?.addressLine}
          />
          {errors.address && !isAddressValid && (
            <p className="mt-1 text-sm text-red-600">
              {t("public_form.common.address_required_error")}
            </p>
          )}

          {/* Customer address suggestion */}
          {shouldShowCustomerSuggestion() && (
            <div className="mt-2">
              <button
                type="button"
                onClick={handleUseCustomerAddress}
                className="p-2 text-left text-xs text-brand bg-brand-muted rounded-lg hover:bg-opacity-70 transition-colors duration-150 focus:outline-none"
              >
                {customer?.primaryAddress?.addressLine},{" "}
                {customer?.primaryAddress?.postCode}{" "}
                {customer?.primaryAddress?.city}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label
            htmlFor="newLocationPostCode"
            value={`${t("public_form.common.postcode")} *`}
            className={labelClass("postCode")}
          />
          <input
            id="newLocationPostCode"
            type="text"
            className={inputClass("postCode")}
            placeholder={t("public_form.common.postcode_placeholder")}
            value={postCode}
            onChange={(e) => handleFieldChange("postCode", e.target.value)}
            autoComplete="off"
          />
          {errors.postCode && (
            <p className="mt-1 text-sm text-red-600">
              {t("public_form.common.postcode")}{" "}
              {t("public_form.common.cargo_description_required")}
            </p>
          )}
        </div>

        <div>
          <Label
            htmlFor="newLocationCity"
            value={`${t("public_form.common.city")} *`}
            className={labelClass("city")}
          />
          <input
            id="newLocationCity"
            type="text"
            className={inputClass("city")}
            placeholder={t("public_form.common.city_placeholder")}
            value={city}
            onChange={(e) => handleFieldChange("city", e.target.value)}
            autoComplete="off"
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-600">
              {t("public_form.common.city")}{" "}
              {t("public_form.common.cargo_description_required")}
            </p>
          )}
        </div>
      </div>

      <div>
        <Label
          htmlFor="newLocationCountry"
          value={t("public_form.common.country")}
        />
        <select
          id="newLocationCountry"
          className={inputClass("country")}
          value={country}
          onChange={(e) => handleFieldChange("country", e.target.value)}
        >
          <option value="NO">{t("public_form.common.norway")}</option>
          <option value="SE">{t("public_form.common.sweden")}</option>
          <option value="DK">{t("public_form.common.denmark")}</option>
          <option value="FI">{t("public_form.common.finland")}</option>
        </select>
      </div>

      <div>
        <Label
          htmlFor="newLocationDescription"
          value={t("public_form.common.description")}
        />
        <textarea
          id="newLocationDescription"
          rows={2}
          className={inputClass("description")}
          placeholder={t("public_form.common.description_placeholder")}
          value={description}
          onChange={(e) => handleFieldChange("description", e.target.value)}
        />
      </div>

      {allowPickupSelection && (
        <div className="pt-4 border-t border-gray-200">
          <Label
            htmlFor="newStopType"
            value={t("public_form.common.stop_type")}
          />
          <div className="flex space-x-4 mt-2">
            <div className="flex items-center">
              <input
                id="newPickup"
                type="radio"
                name="newStopType"
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                checked={isPickup === true}
                onChange={() => setIsPickup(true)}
              />
              <label
                htmlFor="newPickup"
                className="ml-2 text-sm font-medium text-gray-900"
              >
                {t("public_form.common.pickup")}
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="newDelivery"
                type="radio"
                name="newStopType"
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                checked={isPickup === false}
                onChange={() => setIsPickup(false)}
              />
              <label
                htmlFor="newDelivery"
                className="ml-2 text-sm font-medium text-gray-900"
              >
                {t("public_form.common.delivery")}
              </label>
            </div>
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="newDate" value={t("public_form.common.date")} />
        <LGBDatepicker
          inputId="newDate"
          selectedDate={date}
          select={(newDate) => setDate(newDate)}
        />
      </div>

      <div>
        <Label htmlFor="newTiming" value={t("public_form.common.timing")} />
        <select
          id="newTiming"
          className={inputClass("timing")}
          value={timing}
          onChange={(e) => setTiming(e.target.value)}
        >
          <option value="1">{t("public_form.common.before_time")}</option>
          <option value="2">{t("public_form.common.between_times")}</option>
          <option value="3">{t("public_form.common.after_time")}</option>
          <option value="4">{t("public_form.common.earliest_possible")}</option>
          <option value="5">{t("public_form.common.latest_possible")}</option>
        </select>

        {/* Time selectors for before/after/between */}
        {(timing === "1" || timing === "2" || timing === "3") && (
          <div className="mt-3 grid grid-cols-1 gap-3">
            {(timing === "2" || timing === "3") && (
              <div>
                <Label
                  className="text-gray-700 mb-2 text-sm"
                  htmlFor="after-time"
                  value={t("public_form.common.after")}
                />
                <select
                  id="after-time"
                  className={inputClass("afterTime")}
                  value={afterTime}
                  onChange={(e) => setAfterTime(e.target.value)}
                >
                  {generateTimeOptions().map((time) => (
                    <option key={`after-${time}`} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {(timing === "1" || timing === "2") && (
              <div>
                <Label
                  className="text-gray-700 mb-2 text-sm"
                  htmlFor="before-time"
                  value={t("public_form.common.before")}
                />
                <select
                  id="before-time"
                  className={inputClass("beforeTime")}
                  value={beforeTime}
                  onChange={(e) => setBeforeTime(e.target.value)}
                >
                  {generateTimeOptions().map((time) => (
                    <option key={`before-${time}`} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>
    </form>
  );
};

export const PublicRouteModal: React.FC<PublicRouteModalProps> = (props) => {
  const { t } = useTranslation(["order_form", "common"]);

  return (
    <Dialog
      title={props.title}
      show={props.isOpen}
      toggleModal={props.onClose}
      buttonConfirmText={t("public_form.common.save")}
      buttonCloseText={t("common:close")}
      confirmButtonColor="primary"
      buttonConfirmPrependIcon={<HiOutlinePlus className="mr-2 h-5 w-5" />}
      confirmButton={() => {
        document
          .getElementById("public-route-form")
          ?.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true }),
          );
      }}
      content={<PublicRouteForm {...props} />}
    />
  );
};

export default PublicRouteModal;
